import { tokenManager } from '../utils/api';
import { USER_URL } from '../config/environment';
import axios from 'axios';

/**
 * API Key Service
 * Handles all API key-related API calls
 */

export const apiKeyService = {
  /**
   * Get API keys by account
   * @param {string} accountId - Account ID
   * @param {Object} options - Pagination and sorting options
   * @param {number} options.page - Zero-based page index
   * @param {number} options.size - Page size
   * @param {string} options.sortBy - Field to sort by
   * @param {string} options.sortDirection - Sort direction ('ASC' or 'DESC')
   * @returns {Promise<Object>} Paginated API keys response { content, page, size, totalPages, totalElements }
   */
  getApiKeysByAccount: async (accountId, { page = 0, size = 15, sortBy = 'validUntil', sortDirection = 'ASC' } = {}) => {
    const response = await axios.get(`${USER_URL}/api/api-key/account/${accountId}`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` },
      params: { page, size, sortBy, sortDirection }
    });
    // API returns { success, message, data: { ...pagination, content: [...] } }
    return response.data?.data || { content: [], page: 0, size, totalPages: 0, totalElements: 0 };
  },

  /**
   * Generate new API key
   * @param {string} accountId - Account ID
   * @param {string} validityPeriodSeconds - Validity period in seconds
   * @returns {Promise<Object>} Generated API key data
   */
  generateApiKey: async (accountId, validityPeriodSeconds = "31536000") => {
    const response = await axios.post(`${USER_URL}/api/api-key/generate`, {
      accountId,
      validityPeriodSeconds
    }, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Delete API key
   * @param {string} keyId - API key ID
   * @returns {Promise<Object>} Deletion response
   */
  deleteApiKey: async (keyId) => {
    const response = await axios.delete(`${USER_URL}/api/api-key/delete/${keyId}`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Revoke API key
   * @param {string} keyId - API key ID
   * @returns {Promise<Object>} Revoke response
   */
  revokeApiKey: async (keyId) => {
    const response = await axios.delete(`${USER_URL}/api/api-key/revoke`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` },
      data: { id: keyId }
    });
    return response.data;
  }
};

export default apiKeyService;
